<script setup>
import { useEmployabilityStore, useListStore } from "@/stores";
import { computed, onMounted, ref } from "vue";
import drivingLicenses from "@/config/lists/drivingLicenses";

const employabilityStore = useEmployabilityStore();
const listStore = useListStore();

const keyword = computed({
  get: () => employabilityStore.filters.keyword,
  set: (value) => (employabilityStore.filters.keyword = value),
});

const advanced = computed({
  get: () => employabilityStore.filters.advanced,
  set: (value) => (employabilityStore.filters.advanced = value),
});

const appliedFilters = computed(() => {
  const adv = advanced.value;
  return Object.values(adv).filter((val) => {
    if (val == null) return false; // null or undefined
    if (Array.isArray(val)) return val.length > 0; // empty array
    if (typeof val === "string") return val.trim() !== ""; // empty string
    return true; // all other non-null, non-empty values
  }).length;
});

const booleanOptions = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

const emplOfficers = computed(() => listStore.employabilityOfficers);
const services = computed(() => listStore.emplServices);
const branches = computed(() => listStore.branches);
const programs = computed(() => listStore.programs);

const searchBeneficiaries = ref(null);
const beneficiaries = ref([]);

const isLoading = ref(false);

const onInput = async (val) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    beneficiaries.value = await listStore.fetchBeneficiaryIntakes({
      keyword: val,
    });
    isLoading.value = false;
  } else {
    beneficiaries.value = [];
  }
};

const handleResetBtnClick = () => {
  employabilityStore.resetAction();
};

const hideSimpleSearch = computed(() => {
  return false;
});

const languageLevels = [
  { name: "None", value: "NONE" },
  { name: "Basic", value: "BASIC" },
  { name: "Intermediate", value: "INTERMEDIATE" },
  { name: "Advanced", value: "ADVANCED" },
  { name: "Native", value: "NATIVE" },
];

const variant = "employability";

onMounted(async () => {
  await listStore.fetchEmployabilityOfficers();
  await listStore.fetchBranches();
  await listStore.fetchEmplServices();
  await listStore.fetchPrograms();
});
</script>

<template>
  <div v-if="!hideSimpleSearch">
    <v-text-field
      v-model="keyword"
      name="searchbar"
      label="Search (by title, beneficiary name/email)"
    />
  </div>
  <v-expansion-panels class="mb-5">
    <v-expansion-panel :class="`advanced-filters-panel panel-${variant}`">
      <v-expansion-panel-title :class="`title-${variant}`">
        <v-icon icon="mdi-filter" /> <b>Advanced Filters</b>
        <div class="ml-2 text-caption font-weight-bold">
          ({{ appliedFilters }}
          <span v-if="appliedFilters == 1">filter</span>
          <span v-else>filters</span> applied)
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-text-field v-model="advanced.title" name="title" label="Title" />
          </div>

          <div class="flex-1-0-0">
            <v-date-input
              v-model="advanced.date"
              label="Date"
              variant="outlined"
              prepend-icon=""
              multiple="range"
              placeholder="DD/MM/YYYY"
              prepend-inner-icon="$calendar"
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.beneficiary"
              v-model:search-input="searchBeneficiaries"
              :loading="isLoading"
              placeholder="Start typing to Search"
              item-title="name"
              item-value="id"
              :items="beneficiaries"
              variant="outlined"
              hide-details="auto"
              :menu-props="{ maxWidth: 0 }"
              multiple
              chips
              closable-chips
              @update:search="(val) => onInput(val)"
            >
              <template #label> Beneficiary </template>
            </v-autocomplete>
          </div>

          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.emplOfficerId"
              label="Responsible"
              :items="emplOfficers"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.service"
              label="Type of Service"
              :items="services"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.branch"
              label="Branch"
              :items="branches"
              item-title="name"
              item-value="id"
              variant="outlined"
              clearable
            />
          </div>
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.projects"
              label="Odyssea Project"
              :items="programs"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>
        </div>
        <div class="d-flex ga-5 filter-row">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.hasCV"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Has a CV? </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.relocate"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Interested to relocate? </template>
            </v-select>
          </div>

          <div class="flex-1-0-0">
            <v-autocomplete
              v-model="advanced.languages"
              label="Languages"
              :items="listStore.languages"
              item-title="name"
              item-value="id"
              variant="outlined"
              multiple
              clearable
            />
          </div>
          <div class="flex-1-0-0" v-if="advanced.languages.length > 0">
            <v-autocomplete
              v-model="advanced.languageLevels"
              :items="languageLevels"
              label="Language Comprehension Level"
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              multiple
              clearable
            />
          </div>
        </div>
        <div class="d-flex ga-5 mt-5 mb-5 filter-row">
          <div class="flex-1-0-0">
            <v-select
              v-model="advanced.hasDrivingLicense"
              :items="booleanOptions"
              item-title="text"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label> Has driving license? </template>
            </v-select>
          </div>
          <div class="flex-1-0-0" v-if="advanced.hasDrivingLicense">
            <v-select
              v-model="advanced.drivingLicense"
              :items="drivingLicenses"
              item-title="name"
              item-value="value"
              variant="outlined"
              hide-details="auto"
              clearable
            >
              <template #label>
                What type of driver's license do they have?
              </template>
            </v-select>
          </div>
        </div>
        <div class="d-flex ga-5">
          <v-btn
            :color="variant"
            variant="outlined"
            @click="handleResetBtnClick"
          >
            Reset
          </v-btn>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped>
.filter-row {
  flex-wrap: wrap !important;
}

@media (max-width: 599px) {
  .filter-row > div {
    flex: 0 0 100% !important;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .filter-row > div {
    flex: 0 0 calc(50% - 10px) !important;
  }
}
</style>
